import type { ComponentProps } from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { calendarScrollerStyles } from 'tripPlanner/components/Calendar/calendarPanelLayout'

import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlightDealAgentHubDisclaimer from 'components/Flights/FlightMerchandising/FlightDealAgentHubDisclaimer/FlightDealAgentHubDisclaimer'
import FlightDealDuration from 'components/Flights/FlightMerchandising/FlightDealDuration/FlightDealDuration'
import FormattedFlightSearchDate from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchMenu/FormattedFlightSearchDate'
import Group from 'components/utils/Group'
import { FLIGHTS_MAX_LEAD_TIME_DAYS, FLIGHTS_MIN_LEAD_TIME_DAYS, FlightsFareTypes } from 'constants/flight'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import useFeaturedFlightDeals from 'hooks/Flights/useFeaturedFlightDeals'
import { addDays } from 'lib/datetime/dateUtils'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import BodyText from 'LuxKit/Typography/BodyText'
import type { TripStop } from 'tripPlanner/api/types'
import TripContextFlightCalendar from 'tripPlanner/components/Calendar/TripContextFlightCalendar'

/** The panel's own chrome: what is kept for the toggle, footer and padding. */
const PANEL_CHROME_HEIGHT_PX = 170

const CalendarScroller = styled.div`
  ${calendarScrollerStyles(PANEL_CHROME_HEIGHT_PX)}
`

interface Props extends Pick<ComponentProps<typeof DropdownSheet>, 'anchorRef' | 'portaled'> {
  id: string
  formId?: string
  flightDeals?: Array<App.FlightDeal>
  onClose?: () => void
  isOpen: boolean
  /** The trip's stops, drawn behind the dates as read-only context. */
  tripStops?: ReadonlyArray<TripStop>
  /** The stop this flight arrives at, so the calendar opens on its month. */
  contextStopId?: number
}

/**
 * The flight date dropdown, with the trip drawn behind the calendar.
 *
 * Deliberately a trip-planner-owned copy of the standard sheet's shell: the
 * deal banners, the footer and the sheet itself come from the shared flight
 * components untouched, and only the calendar is swapped for one that can show
 * the rest of the itinerary.
 */
function TripContextFlightDateSheet(props: Props) {
  const { anchorRef, portaled, id, formId, onClose, isOpen, flightDeals, tripStops, contextStopId } = props
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const intl = useIntl()
  const departingFormField = useRef<HTMLButtonElement | null>(null)
  const calendarScrollRef = useRef<HTMLDivElement>(null)

  const currentFlight = useMemo(
    () => state.flights.find(flight => flight.id === id),
    [state.flights, id],
  )

  /*
   * Read when the sheet first renders rather than when the module loads: a
   * server process stays up for days, and a bookable window measured at boot
   * would offer departures that have since fallen inside the lead time. Held
   * for the life of the sheet so the calendar's extent cannot change under a
   * scroller anchored against it.
   */
  const earliestBookingDate = useMemo(() => addDays(new Date(), FLIGHTS_MIN_LEAD_TIME_DAYS), [])
  const finalBookingDate = useMemo(() => addDays(new Date(), FLIGHTS_MAX_LEAD_TIME_DAYS), [])

  const checkinDate = currentFlight?.checkinDate
  const checkoutDate = currentFlight?.checkoutDate
  const departureAirport = currentFlight?.departureAirport
  const arrivalAirport = currentFlight?.arrivalAirport
  const { fareType, showSegmentActiveDeals, flightDealId } = state

  useEffect(() => {
    if (fareType.value === FlightsFareTypes.ONE_WAY) {
      // The reducer matches the leg on `id`; without it the clear silently
      // does nothing and a stale return date survives into the search.
      dispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, id, date: null })
    }
  }, [dispatch, fareType.value, id])

  const { featuredRanges, displayDeal } = useFeaturedFlightDeals({
    flightDeals,
    departureAirport,
    arrivalAirport,
    fareType: fareType.value,
    showSegmentActiveDeals,
    flightDealId,
  })

  const showDate = useMemo(() => {
    if (fareType.value === FlightsFareTypes.ONE_WAY) return checkinDate?.isValid()
    return fareType.value === FlightsFareTypes.RETURN
  }, [checkinDate, fareType.value])

  const initialDatesRef = useRef({ checkinDate, checkoutDate })
  const wasOpenRef = useRef(false)

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      initialDatesRef.current = { checkinDate, checkoutDate }
    }
    wasOpenRef.current = isOpen
  }, [checkinDate, checkoutDate, isOpen])

  const handleCancel = useCallback(() => {
    dispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, id, date: initialDatesRef.current.checkinDate ?? null })
    dispatch({
      type: FlightSearchWidgetActions.SET_CHECKOUT_DATE,
      id,
      date: fareType.value === FlightsFareTypes.ONE_WAY ? null : initialDatesRef.current.checkoutDate ?? null,
    })
    onClose?.()
  }, [dispatch, fareType.value, id, onClose])

  return (
    <DropdownSheet
      size="fill-anchor"
      open={isOpen}
      anchorRef={anchorRef}
      portaled={portaled}
      autoHeight={portaled}
      triggerRef={departingFormField}
      onClose={onClose}
      primaryActionProps={{
        'data-testid': 'apply-flight-search-changes',
        type: 'submit',
        form: formId,
        children: intl.formatMessage({
          id: 'qjqe3TY2rAei5vA37jN_',
          defaultMessage: 'Apply',
          description: 'Button text to apply flight date selection changes',
        }),
        onClick: onClose,
      }}
      secondaryActionProps={{
        kind: 'tertiary',
        children: intl.formatMessage({
          id: 'F-YTO1e90A3DqzfDiCrU',
          defaultMessage: 'Cancel',
          description: 'Button text to cancel flight date selection in the flights flow',
        }),
        onClick: handleCancel,
      }}
      footerStart={
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
          <BodyText variant="small" colour="light-foreground-alt">
            {intl.formatMessage({
              id: 'MeAUltVLiiAcd7CwsWte',
              defaultMessage: 'Based on recent return prices (per adult) found',
              description: 'Information text about pricing basis for flight date selection',
            })}
          </BodyText>

          {showDate && (
            <FormattedFlightSearchDate
              checkInDate={checkinDate}
              checkOutDate={checkoutDate}
              fareType={fareType.value}
            />
          )}
        </Group>
      }
    >
      <VerticalSpacer gap={16} key={id}>
        {displayDeal && !isAgentHubEnabled && <FlightDealDuration flightDeal={displayDeal} />}
        {isAgentHubEnabled && <FlightDealAgentHubDisclaimer />}

        <CalendarScroller ref={calendarScrollRef}>
          <TripContextFlightCalendar
            flightId={id}
            tripStops={tripStops}
            contextStopId={contextStopId}
            featuredRanges={featuredRanges}
            minDate={earliestBookingDate}
            maxDate={finalBookingDate}
            scrollContainerRef={calendarScrollRef}
          />
        </CalendarScroller>
      </VerticalSpacer>
    </DropdownSheet>
  )
}

export default TripContextFlightDateSheet
